/**
 * OAuth consent page for Cursor, Claude Desktop, and other MCP clients.
 * Shown when user is redirected from /oauth/authorize (frontend consent_url).
 * User selects org, clicks Allow, then is redirected to client's redirect_uri with auth code.
 */

import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Select,
  SelectItem,
} from '@heroui/react';
import { AlertCircle, LogOut } from 'lucide-react';

import { apiRequest } from '~/services/api/client';
import { getLastOrganization, getMyOrganizations } from '~/services/api/organizations';

import { Feedback } from '~/components/Feedback';
import { Logo } from '~/components/Logo';

import type { Organization } from '~/types';

import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useLogout } from '../hooks/useLogout';
import { logger } from '../utils/logger';

type ConsentStep = 'loading' | 'confirming' | 'processing' | 'error';

/** Human-readable descriptions for OAuth scopes. Matches api/oauth/scopes.py. */
const SCOPE_DESCRIPTIONS: Record<string, string> = {
  openid: 'Verify your identity',
  'projects:read':
    'Read projects, tasks, subtasks, documents, patterns, and reference architectures',
  'projects:write':
    'Create and update projects, tasks, subtasks, documents, and trigger PRD breakdown',
  'embeddings:write': 'Use embeddings',
  'llm:generate': 'Generate AI responses',
};

function scopeToPermissionList(scopeParam: string): Array<{ label: string; scope: string }> {
  const scopes = scopeParam.trim().split(/\s+/).filter(Boolean);
  const seen = new Set<string>();
  const items: Array<{ label: string; scope: string }> = [];
  for (const s of scopes) {
    const desc = SCOPE_DESCRIPTIONS[s];
    if (desc && !seen.has(s)) {
      seen.add(s);
      items.push({ label: desc, scope: s });
    }
  }
  return items.length > 0 ? items : [{ label: 'Access your Compounds account', scope: '' }];
}

export function OAuthConsent(): JSX.Element {
  const { loading: authLoading, user } = useAuth();
  const { handleLogout } = useLogout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSwitchAccount = useCallback((): void => {
    const returnUrl = `/oauth/consent?${searchParams.toString()}`;
    void handleLogout()
      .then(() => navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`))
      .catch(() => navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`));
  }, [handleLogout, navigate, searchParams]);
  const [step, setStep] = useState<ConsentStep>('loading');
  const [error, setError] = useState<string>('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  const state = useMemo(() => searchParams.get('state') ?? '', [searchParams]);
  const clientId = useMemo(() => searchParams.get('client_id') ?? '', [searchParams]);
  const redirectUri = useMemo(() => searchParams.get('redirect_uri') ?? '', [searchParams]);
  const scope = useMemo(() => searchParams.get('scope') ?? '', [searchParams]);
  const codeChallenge = useMemo(() => searchParams.get('code_challenge') ?? '', [searchParams]);
  const codeChallengeMethod = useMemo(
    () => searchParams.get('code_challenge_method') ?? '',
    [searchParams],
  );

  useEffect(() => {
    const initConsent = async (): Promise<void> => {
      if (authLoading) return;

      if (!user) {
        const returnUrl = `/oauth/consent?${searchParams.toString()}`;
        void navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`);
        return;
      }

      try {
        if (!clientId || !redirectUri || !codeChallenge || codeChallengeMethod !== 'S256') {
          throw new Error(
            'Missing or invalid OAuth parameters (client_id, redirect_uri, code_challenge required)',
          );
        }

        const [orgs, lastOrganizationId] = await Promise.all([
          getMyOrganizations(),
          getLastOrganization(),
        ]);
        setOrganizations(orgs);

        if (orgs.length === 0) {
          throw new Error('You must be a member of at least one organization to authorize.');
        }

        // Prefer the org the user last authorized, but only if they are still a member of
        // it — membership can change between authorizations, so a stale pointer must not
        // silently pre-select an org the user can no longer access.
        const validLastOrganization = orgs.find(org => org.id === lastOrganizationId);
        setSelectedOrgId(validLastOrganization?.id ?? orgs[0].id);
        setStep('confirming');
      } catch (err) {
        logger.error(
          { component: 'OAuthConsent' },
          'Init consent error',
          err instanceof Error ? err : new Error(String(err)),
        );
        setStep('error');
        setError(err instanceof Error ? err.message : 'Failed to initialize consent');
      }
    };

    void initConsent();
  }, [
    user,
    authLoading,
    clientId,
    redirectUri,
    codeChallenge,
    codeChallengeMethod,
    searchParams,
    navigate,
  ]);

  const handleAllow = async (): Promise<void> => {
    if (!user || !selectedOrgId) return;

    setStep('processing');
    try {
      const response = await apiRequest<{ redirect_url: string }>('/oauth/consent', {
        method: 'POST',
        body: JSON.stringify({
          state,
          client_id: clientId,
          redirect_uri: redirectUri,
          scope,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          organization_id: selectedOrgId,
        }),
      });

      const redirectUrl = response.data.redirect_url;
      // Route via /oauth/redirect so custom protocol (cursor://) shows "Authorization complete"
      // with Profile/Home buttons; direct window.location.href leaves page stuck on "Redirecting..."
      void navigate(`/oauth/redirect?next=${encodeURIComponent(redirectUrl)}`, { replace: true });
    } catch (err) {
      logger.error(
        { component: 'OAuthConsent' },
        'Consent error',
        err instanceof Error ? err : new Error(String(err)),
      );
      setStep('error');
      setError(err instanceof Error ? err.message : 'Failed to complete authorization');
    }
  };

  const handleDeny = (): void => {
    void navigate('/profile?oauth_denied=true');
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Feedback
          icon={<LoadingSpinner />}
          title="Preparing Authorization"
          description="Loading..."
          type={null}
        />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="flex gap-3">
            <AlertCircle className="text-danger size-6" />
            <p className="text-lg font-bold">Authorization Failed</p>
          </CardHeader>
          <Divider />
          <CardBody>
            <p className="text-default-500">{error}</p>
          </CardBody>
          <CardFooter className="flex justify-end gap-2">
            <Button variant="flat" onPress={() => void navigate('/')}>
              Home
            </Button>
            <Button color="primary" onPress={() => void navigate('/profile')}>
              Profile
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Feedback
          icon={<LoadingSpinner />}
          title="Completing Authorization"
          description="Redirecting..."
          type={null}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="flex flex-col items-center gap-4 p-6 italic">
          <Logo />
          <p className="text-default-500 text-center text-sm">
            An application is requesting access to your Compounds account.
          </p>
        </CardHeader>
        <Divider />
        <CardBody className="p-6 space-y-4">
          {user?.email && (
            <div className="flex items-center justify-between gap-3 p-3 bg-default-50 rounded-lg border border-default-200">
              <p className="text-xs text-default-500 min-w-0">
                Authorizing as{' '}
                <span className="font-medium text-default-700 truncate">{user.email}</span>
              </p>
              <Button
                size="sm"
                variant="light"
                color="danger"
                startContent={<LogOut className="size-3" />}
                onPress={handleSwitchAccount}
                className="shrink-0 text-xs"
              >
                Switch account
              </Button>
            </div>
          )}
          <div className="space-y-2">
            <p className="text-sm font-medium">Select Organization</p>
            <p className="text-xs text-default-400 mb-2">
              The application will have access to projects and tasks within this organization.
            </p>
            {organizations.length > 1 ? (
              <Select
                label="Organization"
                placeholder="Select an organization"
                selectedKeys={[selectedOrgId]}
                onSelectionChange={keys => setSelectedOrgId(Array.from(keys)[0] as string)}
                variant="bordered"
              >
                {organizations.map(org => (
                  <SelectItem key={org.id} textValue={org.name}>
                    {org.name}
                  </SelectItem>
                ))}
              </Select>
            ) : (
              <div className="p-3 bg-default-100 rounded-lg border border-default-200">
                <p className="font-semibold">{organizations[0]?.name}</p>
                <p className="text-xs text-default-400">Single organization available</p>
              </div>
            )}
          </div>

          <div className="p-3 bg-warning/10 rounded-lg space-y-1">
            <p className="text-xs font-bold text-warning-600 uppercase">Permissions</p>
            <ul className="text-xs text-default-600 list-disc list-inside space-y-1">
              {scopeToPermissionList(scope).map(item => (
                <li key={item.scope || 'default'}>
                  {item.label}
                  {item.scope && ` (${item.scope})`}
                </li>
              ))}
            </ul>
          </div>
        </CardBody>
        <CardFooter className="p-6 flex flex-col gap-3">
          <Button
            color="primary"
            className="w-full font-bold"
            size="lg"
            onPress={() => void handleAllow()}
          >
            Allow
          </Button>
          <Button variant="light" className="w-full" onPress={handleDeny}>
            Deny
          </Button>
          <p className="text-[10px] text-center text-default-400">
            By clicking Allow, you authorize this application to access your data as described
            above.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
